/**
 * Admin login.
 *
 * A plain link to a server route, not Google's in-page button: that button
 * loads third-party JavaScript into the app shell, and the whole point of the
 * server-side Authorization Code + PKCE flow is that nothing from Google runs
 * here. The `<a href>` below is the entire client-side implementation.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';

/**
 * The server redirects back with `?fout=<reason>`. Each of these means nothing
 * was written — no account, no cookie, only a line in the audit log.
 */
const REASONS: Record<string, string> = {
  'not-allowed':
    'Dit Google-account staat niet op de lijst. Beheer toevoegen kan alleen in .env op de server.',
  unverified: 'Dit Google-account heeft geen bevestigd e-mailadres.',
  'sub-mismatch':
    'Dit e-mailadres hoort bij een ander Google-account dan de eerste keer. Uit voorzorg geweigerd — zie de README als het adres echt opnieuw is uitgegeven.',
  'bad-client':
    'Google weigert de client-id of de client-secret van deze server. Controleer GOOGLE_CLIENT_SECRET in .env — een geplakte secret bevat nog wel eens een teken te veel. De precieze melding van Google staat in het serverlog.',
  'bad-state': 'De inlogpoging was verlopen of al gebruikt. Probeer het opnieuw.',
  'exchange-failed': 'Google gaf geen bruikbaar antwoord. Probeer het opnieuw.',
  onvolledig: 'Google stuurde een onvolledig antwoord terug.',
  onbekend: 'Er ging iets mis bij het starten van het inloggen.',
};

export default function BeheerLogin() {
  const [params] = useSearchParams();
  const [available, setAvailable] = useState<boolean | null>(null);
  const fout = params.get('fout');

  useEffect(() => {
    void api<{ available: boolean }>('/api/auth/google/available')
      .then((body) => setAvailable(body.available))
      .catch(() => setAvailable(null));
  }, []);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 px-6 text-center text-white">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Beheer</h1>
        <p className="text-slate-400">Alleen voor wie de tafel beheert.</p>
      </div>

      {fout && (
        <p className="max-w-sm rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {REASONS[fout] ?? 'Inloggen is niet gelukt.'}
        </p>
      )}

      {available === false ? (
        <p className="max-w-sm text-sm text-slate-500">
          Google-inloggen is nog niet ingesteld op deze server. Zet <code>GOOGLE_CLIENT_ID</code> en{' '}
          <code>GOOGLE_CLIENT_SECRET</code> in <code>.env</code> en herstart.
        </p>
      ) : (
        /* A real navigation, not fetch: the flow leaves this origin. */
        <a
          href="/api/auth/google/start"
          className="min-h-12 rounded-xl bg-white px-6 py-3 font-medium text-base-950 hover:bg-slate-200"
        >
          Inloggen met Google
        </a>
      )}

      <Link to="/" className="text-sm text-slate-600 underline underline-offset-4">
        Terug
      </Link>
    </div>
  );
}
